import { RemoveTagEvent } from '@progress/kendo-angular-dropdowns';
import { IFieldDefinitionLogic } from '../../interfaces/ifield-definition-logic.interface';
import { IViewModel } from '../../interfaces/view-model.interface';
import { FieldDefinitionService } from '../../services/field-definition.service';
import { FormField } from '../form-builder/form-field.model';
import { FormInstanceElement } from '../form-builder/form-instance-element.model';
import { CustomDateFormatDirective } from '../../directives/custom-date-format.directive';

export class FormInstanceGridRow implements IViewModel {
    // meta data
    public id: number;

    //VNEXT-1099 - KLW: Adding fields to Site Homepage
    public createdBy: string;
    public createdByDisplayName: string; // Added 06-05-2024
    public createdDate: Date;
    public modifiedBy: string;
    public modifiedByDisplayName: string; // Added 06-05-2024
    public modifiedDate: Date;

    public name: string;
    public formTemplateName: string;
    public linkedFormInstance: boolean;
    // chose to name this favorite instead of isFavorite (same in server view model)
    // to keep the display of the Home Page Grid column name simple and not require aliasing
    // (The favorite column being a special case)
    public favorite: boolean;
    public isReference: boolean;
    public isReferenced: boolean;
    public dataCollectionId: number;
    public folderId: number;
    public folderName: string;
    public folderPath: string;
    public workflowState: string;
    public isFirstGridRow: boolean;

    // pharvey - added for VNEXT-983 - home page actions
    public canMoveFormInstance: boolean;
    public canCopyFormInstance: boolean;
    public canDeleteFormInstance: boolean;
    public canRenameFormInstance: boolean;
    public canCreateFolder: number;
    public canDeleteFolder: number;
    public canCreateForm: number;
    public canDeleteForm: number;

    public rowData: FormInstanceElement[];
    private static columnDefinitions: FormField[];

    //VNEXT-1099 - KLW: Used for custom date format strings
    private getMetaDataValuePropertyNames_DateFormatting: string[] = ["createdDate", "modifiedDate"];

    public static getAsPlainObject(fiGridRow: FormInstanceGridRow, columnDefinitions: FormField[], fieldDefinitionService: FieldDefinitionService): object {
        FormInstanceGridRow.columnDefinitions = columnDefinitions;

        let obj = {};
        let formName = fiGridRow.isReference ? `${fiGridRow.name} (Ref)` : fiGridRow.name;

        obj[FormInstanceGridRow.getFieldName('Id')] = fiGridRow.getMetaDataValue('id');

        //VNEXT-1099 - KLW: Adding fields to Site Homepage
        obj[FormInstanceGridRow.getFieldName('CreatedBy')] = fiGridRow.getMetaDataValue('createdByUserName');
        obj[FormInstanceGridRow.getFieldName('CreatedByDisplayName')] = fiGridRow.getMetaDataValue('createdByDisplayName');
        obj[FormInstanceGridRow.getFieldName('CreatedDate')] = fiGridRow.getMetaDataValue('createdDate');
        obj[FormInstanceGridRow.getFieldName('ModifiedBy')] = fiGridRow.getMetaDataValue('modifiedByUserName');
        obj[FormInstanceGridRow.getFieldName('ModifiedByDisplayName')] = fiGridRow.getMetaDataValue('modifiedByDisplayName');
        obj[FormInstanceGridRow.getFieldName('ModifiedDate')] = fiGridRow.getMetaDataValue('modifiedDate');

        obj[FormInstanceGridRow.getFieldName('Name')] = formName;
        obj[FormInstanceGridRow.getFieldName('FormTemplateName')] = fiGridRow.getMetaDataValue('formTemplateName');
        obj[FormInstanceGridRow.getFieldName('LinkedFormInstance')] = fiGridRow.getMetaDataValue('linkedFormInstance');
        obj[FormInstanceGridRow.getFieldName('Favorite')] = fiGridRow.getMetaDataValue('favorite');
        obj[FormInstanceGridRow.getFieldName('Subscription')] = fiGridRow.getMetaDataValue('subscription');
        obj[FormInstanceGridRow.getFieldName('IsReference')] = fiGridRow.getMetaDataValue('isReference');
        obj[FormInstanceGridRow.getFieldName('IsReferenced')] = fiGridRow.getMetaDataValue('isReferenced');
        obj[FormInstanceGridRow.getFieldName('FolderId')] = fiGridRow.getMetaDataValue('folderId');
        obj[FormInstanceGridRow.getFieldName('FolderName')] = fiGridRow.getMetaDataValue('folderName');
        obj[FormInstanceGridRow.getFieldName('FolderPath')] = fiGridRow.getMetaDataValue('folderPath');
        obj[FormInstanceGridRow.getFieldName('WorkflowState')] = fiGridRow.getMetaDataValue('workflowState');
        obj[FormInstanceGridRow.getFieldName('FormOpenUrl')] = `site/${fiGridRow.dataCollectionId}/structure/folder/${fiGridRow.folderId}/form/${fiGridRow.id}`;

        obj['canCopy'] = fiGridRow.canCopyFormInstance;
        obj['canMove'] = fiGridRow.canMoveFormInstance;
        obj['canDelete'] = fiGridRow.canDeleteFormInstance;
        obj['isFirstGridRow'] = fiGridRow.isFirstGridRow;

        obj['Key'] = fiGridRow['key'];
        obj['Member'] = fiGridRow['member'];
        obj['ItemCount'] = fiGridRow['itemCount'];
        obj['HasSubgroups'] = fiGridRow['hasSubgroups'];
        obj['Items'] = fiGridRow['items'];
        obj['Aggregates'] = {};
        obj['SubgroupCount'] = 0;
        obj['Subgroups'] = [];

        // pharvey - 11/29/2023 - new approach for populating the object - it handles the case where a column does not have a matching formInstanceElement
        // in fiGridRow.rowData
        let nonMetaDataFields = columnDefinitions.filter(x => { return x.id > 0 });

        // IMPORTANT: One of the complexities of Home Page Grids is that the columns have to be
        // carefully managed. Maybe there's a better way. For now the columnDefinitions for the
        // Home Page Grid are passed into this method, and are used to determine which properties to set
        // on the plain JS object that will be returned.
        // The columnDefinitions gets passed in a chain of FormTemplateINstancesComponent --> FormTemplateInstancesGridRemoteBindingDirective
        // --> FormTemplateINstancesService --> this method. (They are built on the server in FormTemplateInstanceService.GetFormFields())
        // It does seem that the server should be able to handle more/all of this and so reduce the complexity of
        // the client code.
        for (let colField of nonMetaDataFields) {
            // try and get a FormInstanceElement...
            let fieldName = colField.displayName || colField.name;
            let fiElement = fiGridRow.rowData != null ? fiGridRow.rowData.filter(x => { return x.formField.name == fieldName || x.formField.displayName == fieldName })[0] : null;
            let field = null;
            if (fiElement) {
                field = fiElement.formField;
            } else {
                field = colField;
                fiElement = new FormInstanceElement();
            }

            field.readOnly = true;
            let fieldDefClientLogic: IFieldDefinitionLogic = fieldDefinitionService.getFieldClientLogicHandler(field.fieldDefinitionClassName);
            let displayVal = fieldDefClientLogic.getDisplayValue(field, fiElement, null, null);
            let name = FormInstanceGridRow.getFieldName((field.displayName || field.name));
            obj[name] = displayVal;
        }

        //Keeping this for now -- the old way was to loop over the rowData returned from the server BUT that did not include anything for empty
        //Grids, and so non-selected checkboxes were displaying as blank cells. 
        //for (let fiElement of fiGridRow.rowData) {
        //    //VNEXT-981 - KLW: Changes to display a checkbox on the Site Homepage as disabled, set the readonly of formfield to true, the site home page should be the only component using this method
        //    fiElement.formField.readOnly = true;

        //    let fieldDefClientLogic: IFieldDefinitionLogic = fieldDefinitionService.getFieldClientLogicHandler(fiElement.formField.fieldDefinitionClassName);
        //    let displayVal = fieldDefClientLogic.getDisplayValue(fiElement.formField, fiElement, null, null);
        //    let name = FormInstanceGridRow.getFieldName((fiElement.formField.displayName || fiElement.formField.name));

        //    obj[name] = displayVal;
        //}

        return obj;
    }

    public getMetaDataValue(property: string): string {
        //if (this.isFirstGridRow) {
        // return this[property];
        //} else {
        //    return "";
        //}

        //VNEXT-1099 - KLW: Change how the getting the value for the meta fields to account for date types with a custom string format
        let retVal = this[property];

        if (this.getMetaDataValuePropertyNames_DateFormatting.includes(property)) {
            //https://www.w3schools.com/angular/ng_filter_date.asp
            retVal = CustomDateFormatDirective.applyDateFormattingUsing("yyyy-MM-dd HH:mm:ss", retVal);
        }

        return retVal;
    }

    // Accounts for the fact that a column name may have been aliased and if so we need to use that as the property name instead of displayName or name
    private static getFieldName(name: string): string {
        let column = FormInstanceGridRow.columnDefinitions.filter(x => { return x.displayName == name || x.name == name })[0];
        if (column) {
            return column.alias?.trim().length > 0 ? column.alias : name;
        } else {
            return name;
        }
    }
}
